/**
 * DPCM channel wrapper.
 *
 * Loads the DPCM AudioWorklet module, exposes a thin API for uploading
 * samples (one-time per session) and triggering them. DSP lives in
 * dsp/dpcm.ts so it can be Vitest-tested without an AudioContext.
 */

let workletLoaded = false;

export async function loadDPCMWorklet(ctx: AudioContext): Promise<void> {
  if (workletLoaded) return;
  await ctx.audioWorklet.addModule('/worklets/dpcm.js');
  workletLoaded = true;
}

export interface DPCMNode {
  /** Upload a sample so it can be triggered later by name. */
  loadSample(name: string, data: Float32Array, sourceSampleRate: number): void;
  /** Re-trigger the named sample from position 0. One-shot unless loop=true. */
  trigger(name: string, loop?: boolean): void;
  /** Stop playback immediately. */
  stop(): void;
  setRateIndex(index: number): void;
  setVolume(volume: number): void;
  destroy(): void;
}

export function createDPCMNode(ctx: AudioContext): DPCMNode {
  const node = new AudioWorkletNode(ctx, 'dpcm-processor', {
    numberOfInputs: 0,
    numberOfOutputs: 1,
    outputChannelCount: [1],
  });
  node.connect(ctx.destination);

  const set = (name: 'rateIndex' | 'volume', value: number) => {
    const p = node.parameters.get(name);
    if (p) p.value = value;
  };

  return {
    loadSample(name, data, sourceSampleRate) {
      // Copy the data so the caller can keep using it; the worklet now owns
      // its own buffer. (We could mark it transferable for speed, but then
      // the original buffer would be neutered, which is surprising.)
      const copy = new Float32Array(data);
      node.port.postMessage(
        { type: 'loadSample', name, data: copy, sourceSampleRate },
        [copy.buffer],
      );
    },
    trigger(name, loop = false) {
      node.port.postMessage({ type: 'trigger', name, loop });
    },
    stop: () => node.port.postMessage({ type: 'stop' }),
    setRateIndex: (idx) => set('rateIndex', Math.max(0, Math.min(15, Math.round(idx)))),
    setVolume: (vol) => set('volume', vol),
    destroy: () => {
      node.port.postMessage({ type: 'stop' });
      node.disconnect();
    },
  };
}
